import { CartApiError } from '../api/cartApi.types';
import { AppErrorBase } from './classes';
import { ErrorCode } from './catalog';
import type { AppError, AppErrorContext } from './types';
import { parseStockError } from '../utils/cartRules';

type NormalizeHints = {
  context?: Partial<AppErrorContext>;
  code?: ErrorCode;
  level?: AppError['level'];
  retryable?: boolean;
  message?: string;
};

function isAppError(error: unknown): error is AppError {
  return typeof error === 'object' && error !== null && 'code' in error && 'timestamp' in error;
}

export function normalizeError(error: unknown, hints?: NormalizeHints): AppError {
  const applyHints = (base: AppError): AppError => ({
    ...base,
    code: hints?.code ?? base.code,
    message: hints?.message ?? base.message,
    level: hints?.level ?? base.level,
    retryable: hints?.retryable ?? base.retryable,
    context: { ...(base.context ?? {}), ...(hints?.context ?? {}) }
  });

  if (isAppError(error)) {
    const base = {
      ...error,
      timestamp: error.timestamp || Date.now(),
      context: { ...(error.context ?? {}) }
    };
    return applyHints(base);
  }

  if (error instanceof CartApiError) {
    const raw = error.raw as
      | { description?: string; message?: string; errors?: string[]; status?: number; statusCode?: number }
      | undefined;
    const rawStatus = typeof raw?.status === 'number' ? raw.status : raw?.statusCode;
    const status = typeof error.status === 'number' ? error.status : rawStatus;
    if (error.type === 'SHOPIFY_VALIDATION' && status === 422) {
      const message = raw?.description || raw?.message || raw?.errors?.[0] || error.message;
      const parsed = parseStockError(message);
      const base: AppError = {
        code: ErrorCode.CART_STOCK_LIMIT,
        message,
        level: 'error',
        retryable: false,
        timestamp: Date.now(),
        context: {
          status: error.status,
          apiMessage: error.message,
          maxQty: parsed.maxQty
        },
        cause: error
      };
      return applyHints(base);
    }

    const mappedCode: ErrorCode =
      error.type === 'NETWORK'
        ? ErrorCode.NETWORK
        : error.type === 'TIMEOUT'
          ? ErrorCode.TIMEOUT
          : error.type === 'RATE_LIMIT'
            ? ErrorCode.RATE_LIMIT
            : error.type === 'SHOPIFY_VALIDATION'
              ? ErrorCode.VALIDATION
              : ErrorCode.UNKNOWN;

    const base: AppError = {
      code: mappedCode,
      message: error.message,
      level: 'error',
      retryable: error.type === 'NETWORK' || error.type === 'TIMEOUT' || error.type === 'RATE_LIMIT',
      timestamp: Date.now(),
      context: {
        status: error.status,
        apiMessage: error.message
      },
      cause: error
    };
    return applyHints(base);
  }

  if (error instanceof AppErrorBase) {
    const retryable =
      error.code === ErrorCode.NETWORK ||
      error.code === ErrorCode.TIMEOUT ||
      error.code === ErrorCode.RATE_LIMIT;

    const base: AppError = {
      code: error.code,
      message: error.message,
      level: retryable ? 'warning' : 'error',
      retryable,
      timestamp: Date.now(),
      context: { ...(error.context ?? {}) },
      cause: error.cause ?? error
    };
    return applyHints(base);
  }

  if (error instanceof Error) {
    const base: AppError = {
      code: ErrorCode.UNKNOWN,
      message: error.message,
      level: 'error',
      retryable: false,
      timestamp: Date.now(),
      context: {},
      cause: error
    };
    return applyHints(base);
  }

  const base: AppError = {
    code: ErrorCode.UNKNOWN,
    message: 'Unknown error',
    level: 'error',
    retryable: false,
    timestamp: Date.now(),
    context: {},
    cause: error
  };
  return applyHints(base);
}
